<template>

  <div class="content-block dx-card">
    <h2>Sites</h2> <!-- ❯ = unicode U+276F  -->
  </div>

  <div class="content-block dx-card" id="dataDiv">
    <DxDataGrid
      v-if="columnsReady"
      class="dx-card wide-card"
      :data-source="sites"
      :key-expr="containerId"
      :hover-state-enabled="true"
      :selection="{ mode: 'single' }"
      :show-borders="true"
      :columns-auto-width="true"
      :column-min-width="50"
      :allow-column-resizing="true"
      column-resizing-mode="widget"
      :allow-column-reordering="true"
      :filter-builder="filterBuilder"
      :style="{ 'max-height': datagridHeight }"
      no-data-text="No data."
      @initialized="saveGridInstance"
    >
      <!-- @selection-changed="onSelectionChanged" -->
      <!-- <DxLoadPanel :enabled="true" /> -->
      <DxStateStoring :enabled="true" type="localStorage" storage-key="system-sites-grid" />
      <DxColumnChooser :enabled="true" mode="select" />
      <DxFilterRow :visible="true" />
      <DxHeaderFilter :visible="true" />
      <DxScrolling mode="standard" />
      <DxExport :enabled="true" />
      <DxColumnFixing :enabled="true" />
      <DxPager
        :show-info="true"
        :show-page-size-selector="true"
        :allowed-page-sizes="[20, 50, 100]"
        :show-navigation-buttons="true"
      />

      <DxToolbar>
        <!-- <DxItem location="before" template="addRecordTemplate" /> -->
        <DxItem location="after" template="refreshRecordTemplate" />
        <DxItem location="after" template="removeFilterRecordTemplate" />
        <DxItem location="after" name="exportButton" />
        <DxItem location="after" name="columnChooserButton" />
      </DxToolbar>

      <!-- <template #addRecordTemplate>
        <DxButton icon="add" @click="newRec" />
      </template> -->
      <template #refreshRecordTemplate>
        <DxButton icon="refresh" @click="refreshData" hint="Refresh Table" />
      </template>
      <template #removeFilterRecordTemplate>
        <DxButton icon="filter" @click="removeFilter" hint="Clear filters and sorting" />
      </template>

      <DxColumn
        v-for="column in resolvedColumns"
        :key="column.dataField"
        :data-field="column.dataField"
        :caption="column.caption"
        :visible="column.visible"
      />

    </DxDataGrid>
  </div>

  <DxLoadPanel
    v-model:visible="loadingVisible"
    :message="Loading"
    :show-indicator="true"
    :show-pane="true"
    :shading="true"
    :hide-on-outside-click="false"
    :on-shown="onLoadPanelShown"
    :on-hidden="onLoadPanelHidden"
    shading-color="rgba(0,0,0,0.4)"
  />
</template>

<script>

import {
  DxDataGrid,
  DxHeaderFilter,
  DxFilterRow,
  DxScrolling,
  DxColumnChooser,
  DxColumnFixing,
  DxPager,
  DxItem,
  DxToolbar,
  DxExport,
  DxStateStoring,
  DxColumn
} from "devextreme-vue/data-grid";
import { DxButton } from "devextreme-vue/button";
import { DxLoadPanel } from "devextreme-vue/load-panel";

export default {
  components: {
    DxDataGrid,
    DxColumn,
    DxHeaderFilter,
    DxFilterRow,
    DxScrolling,
    DxColumnChooser,
    DxLoadPanel,
    DxButton,
    DxToolbar,
    DxItem,
    DxExport,
    DxColumnFixing,
    DxPager,
    DxStateStoring
  },
  data() {
    return {
      // columnResize: "widget",
      dataGridInstance: null,
      columnsReady: false,
      resolvedColumns: [],
      loadingVisible: false,
      isLoadPanelVisible: false,
      datagridHeight: "40vh",
      currentArea: null,
      routes: [],
      defaultColumns: [
        { dataField: "deviceDetails[0].imei", caption: "Serial#" },
        { dataField: "customerName", caption: "Customer Name" },
        { dataField: "operator", caption: "Operator" },
        { dataField: "area", caption: "Area" },
        { dataField: "containerName", caption: "Surface" },
        { dataField: "alias", caption: "Alias" },
        { dataField: "downhole lsd", caption: "Downhole LSD" },
        { dataField: "product", caption: "Products" },
        { dataField: "dte", caption: "DTE" },
        { dataField: "volume (l)", caption: "Volume (L)" },
        { dataField: "last device communication", caption: "Last Device Communication" }
      ],
    };
  },
  created() {
    this.loadPageData();
  },
  mounted() {
    window.addEventListener('resize', this.setDimensions);
    this.setDimensions();
  },
  unmounted() {
    window.removeEventListener('resize', this.setDimensions);
  },  
  computed: {
    sites() {
      return this.$store.getters["siteList/getSystemSites"]
    },
  },
  methods: {
    // Merges defaultColumns (always visible) with every other field found in
    // the data source (including nested objects/arrays) so they show up as
    // hidden-but-toggleable entries in the column chooser. Declaring an
    // explicit DxColumn per field (rather than leaving columns unspecified)
    // is required for DxStateStoring to persist column-visibility toggles
    // across reloads.
    //
    // This is computed exactly ONCE, right after data first loads (see
    // loadPageData), rather than as a reactive computed property. A reactive
    // computed tied to `sites` would rebuild a brand-new array (with the
    // hardcoded default `visible` values) every time the data reloads, and
    // that new array would stomp over whatever DxStateStoring had already
    // restored from localStorage - which is why toggles weren't persisting.
    buildGridColumns(rows) {
      const defaultFields = new Set(this.defaultColumns.map((c) => c.dataField));
      const extraFields = new Set();
      const list = Array.isArray(rows) ? rows : [];
      const sampleSize = Math.min(list.length, 100);

      for (let i = 0; i < sampleSize; i++) {
        const row = list[i];
        if (!row || typeof row !== "object") continue;
        for (const [key, value] of Object.entries(row)) {
          if (defaultFields.has(key)) continue;
          this.collectFieldPaths(value, key, 0, extraFields);
        }
      }

      const extraColumns = Array.from(extraFields)
        .filter((dataField) => !defaultFields.has(dataField))
        .sort()
        .map((dataField) => ({
          dataField,
          caption: this.humanizeField(dataField),
          visible: false,
        }));

      return [
        ...this.defaultColumns.map((c) => ({ ...c, visible: true })),
        ...extraColumns,
      ];
    },
    // Recursively walks nested plain objects (dot notation) and arrays of
    // objects (bracket index notation, e.g. "deviceDetails[0].imei") to build
    // DevExtreme-compatible dataField paths, up to a shallow depth.
    collectFieldPaths(value, path, depth, fields) {
      if (Array.isArray(value)) {
        if (depth >= 3) return;
        if (value.length && value[0] && typeof value[0] === "object") {
          this.collectFieldPaths(value[0], `${path}[0]`, depth + 1, fields);
        }
        return;
      }
      if (value !== null && typeof value === "object") {
        if (depth >= 3) return;
        for (const [key, nested] of Object.entries(value)) {
          this.collectFieldPaths(nested, `${path}.${key}`, depth + 1, fields);
        }
        return;
      }
      fields.add(path);
    },
    humanizeField(field) {
      return field
        .replace(/\[\d+\]/g, "")
        .split(".")
        .map((part) =>
          part
            .replace(/[_-]+/g, " ")
            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
            .trim()
        )
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    },
    onLoadPanelShown() {
      setTimeout(() => {
        this.loadingVisible = false;
      }, 10000);
    },
    onLoadPanelHidden() {},
    saveGridInstance(e) {
      this.dataGridInstance = e.component;
    },
    refreshData() {
      this.loadPageData(true);
    },
    removeFilter() {
      if (this.dataGridInstance) {
        this.dataGridInstance.clearFilter();
        this.dataGridInstance.clearSorting();
      }
    },
    setDimensions() {
      const h = document.documentElement.clientHeight;
      this.datagridHeight = (h - 250) + 'px';
    },
    async loadPageData(refresh = false) {
      this.loadingVisible = true;
      try {
        await this.$store.dispatch("siteList/loadAllSites", 1);
      } catch (error) {
        this.error = error.message || "Unexpected Error.";
      }
      if (!this.columnsReady) {
        this.resolvedColumns = this.buildGridColumns(this.sites);
        this.columnsReady = true;
      }
      this.loadingVisible = false;
    },
  }
};
</script>

<style scoped>
h2 {
  margin-left: 1em;
}
</style>
